import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  collection, getDocs, doc, updateDoc, deleteDoc, query, where
} from 'firebase/firestore';
import { db } from '../../firebase';
import { getProjectMemberPath } from '../../utils/collectionPaths';
import { inviteUserToProject } from '../../services/apiService';
import { removeUserProject, updateUserProjectRole, getUserByEmail } from '../../services/userService';

// MUI Imports
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import TableSortLabel from '@mui/material/TableSortLabel';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';

// Icons
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// Email validation regex
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// Helper function to validate email format
const validateEmail = (email) => {
  return EMAIL_REGEX.test(email);
};

// Helper function to get project members
const getProjectMembers = async (projectId) => {
  try {
    const membersRef = collection(db, `projects/${projectId}/members`);
    const membersSnap = await getDocs(membersRef);
    return membersSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting project members:', error);
    return [];
  }
};

// Helper function to check if user is already a member
const isUserAlreadyMember = (members, email) => {
  return members.some(member => member.email === email);
};

const toDate = (timestamp) => {
  if (!timestamp) return null;
  return timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
};

const isInvitationExpired = (invitation) => {
  const expiresAt = toDate(invitation?.expiresAt);
  return Boolean(expiresAt && new Date() > expiresAt);
};

const getInvitationDisplayStatus = (invitation) => {
  if (invitation?.status === 'pending' && isInvitationExpired(invitation)) {
    return 'expired';
  }
  return invitation?.status;
};

const getInvitationLink = (invitation) => {
  if (!invitation?.token) return '';
  return `${window.location.origin}/invite?token=${invitation.token}`;
};

function UsersPermissionsDialog({ open, onClose, projectId, projectName, currentUser }) {
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingMember, setEditingMember] = useState(null);
  const [newRole, setNewRole] = useState('');

  // Add member state
  const [addMemberModalOpen, setAddMemberModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [inviteLinkToShow, setInviteLinkToShow] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendError, setResendError] = useState('');
  const [resendSuccess, setResendSuccess] = useState('');

  // Sorting state for members table
  const [membersSortField, setMembersSortField] = useState('isCurrentUser');
  const [membersSortDirection, setMembersSortDirection] = useState('desc');

  // Sorting state for invitations table
  const [invitationsSortField, setInvitationsSortField] = useState('createdAt');
  const [invitationsSortDirection, setInvitationsSortDirection] = useState('desc');

  // Menu state for members
  const [membersMenuAnchorEl, setMembersMenuAnchorEl] = useState(null);
  const [membersMenuMemberId, setMembersMenuMemberId] = useState(null);

  // Menu state for invitations
  const [invitationsMenuAnchorEl, setInvitationsMenuAnchorEl] = useState(null);
  const [invitationsMenuInvitationId, setInvitationsMenuInvitationId] = useState(null);

  // Sort function for members
  const sortMembers = (membersList) => {
    return [...membersList].sort((a, b) => {
      // Always put current user first
      const aIsCurrentUser = a.id === currentUser?.uid;
      const bIsCurrentUser = b.id === currentUser?.uid;
      
      if (aIsCurrentUser && !bIsCurrentUser) return -1;
      if (!aIsCurrentUser && bIsCurrentUser) return 1;
      
      // If both are current user or both are not current user, sort by selected field
      let aValue, bValue;
      
      switch (membersSortField) {
        case 'email':
          aValue = a.email?.toLowerCase() || '';
          bValue = b.email?.toLowerCase() || '';
          break;
        case 'role':
          aValue = a.role || '';
          bValue = b.role || '';
          break;
        case 'joinedAt':
          aValue = a.joinedAt?.toDate ? a.joinedAt.toDate().getTime() : new Date(a.joinedAt).getTime();
          bValue = b.joinedAt?.toDate ? b.joinedAt.toDate().getTime() : new Date(b.joinedAt).getTime();
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return membersSortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return membersSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Sort function for invitations
  const sortInvitations = (invitationsList) => {
    return [...invitationsList].sort((a, b) => {
      let aValue, bValue;
      
      switch (invitationsSortField) {
        case 'recipientEmail':
          aValue = a.recipientEmail?.toLowerCase() || '';
          bValue = b.recipientEmail?.toLowerCase() || '';
          break;
        case 'roleToAssign':
          aValue = a.roleToAssign || '';
          bValue = b.roleToAssign || '';
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'expiresAt':
          aValue = a.expiresAt?.toDate ? a.expiresAt.toDate().getTime() : new Date(a.expiresAt).getTime();
          bValue = b.expiresAt?.toDate ? b.expiresAt.toDate().getTime() : new Date(b.expiresAt).getTime();
          break;
        case 'createdAt':
          aValue = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt).getTime();
          bValue = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt).getTime();
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return invitationsSortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return invitationsSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Handle members table sort
  const handleMembersSort = (field) => {
    if (membersSortField === field) {
      setMembersSortDirection(membersSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setMembersSortField(field);
      setMembersSortDirection('asc');
    }
  };

  // Handle invitations table sort
  const handleInvitationsSort = (field) => {
    if (invitationsSortField === field) {
      setInvitationsSortDirection(invitationsSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setInvitationsSortField(field);
      setInvitationsSortDirection('asc');
    }
  };

  // Handle members menu
  const handleMembersMenuOpen = (event, memberId) => {
    event.stopPropagation();
    setMembersMenuAnchorEl(event.currentTarget);
    setMembersMenuMemberId(memberId);
  };

  const handleMembersMenuClose = () => {
    setMembersMenuAnchorEl(null);
    setMembersMenuMemberId(null);
  };

  // Handle invitations menu
  const handleInvitationsMenuOpen = (event, invitationId) => {
    event.stopPropagation();
    setInvitationsMenuAnchorEl(event.currentTarget);
    setInvitationsMenuInvitationId(invitationId);
  };

  const handleInvitationsMenuClose = () => {
    setInvitationsMenuAnchorEl(null);
    setInvitationsMenuInvitationId(null);
  };

  // Add member functions
  const handleOpenAddMemberModal = () => {
    setInviteEmail('');
    setInviteRole('member');
    setInviteError('');
    setInviteSuccess('');
    setAddMemberModalOpen(true);
  };

  const handleCloseAddMemberModal = () => {
    setAddMemberModalOpen(false);
    setInviteLinkToShow('');
  };

  const handleCopyInviteLink = () => {
    navigator.clipboard.writeText(inviteLinkToShow);
  };

  const handleCopyExistingInvitationLink = async (invitation) => {
    const inviteLink = getInvitationLink(invitation);
    if (!inviteLink) {
      setResendError('This invitation does not have a link to copy.');
      return;
    }

    try {
      await navigator.clipboard.writeText(inviteLink);
      setResendSuccess(`Invitation link copied for ${invitation.recipientEmail}`);
      setTimeout(() => setResendSuccess(''), 5000);
    } catch (err) {
      console.error('Error copying invitation link:', err);
      setResendError('Failed to copy invitation link.');
    }
  };

  const handleInvite = async () => {
    setInviteLoading(true);
    setInviteError('');
    setInviteSuccess('');
    setInviteLinkToShow('');

    try {
      // 1. Validate email format
      if (!inviteEmail.trim()) {
        setInviteError('Email address is required.');
        setInviteLoading(false);
        return;
      }

      if (!validateEmail(inviteEmail.trim())) {
        setInviteError('Please enter a valid email address.');
        setInviteLoading(false);
        return;
      }

      const emailToInvite = inviteEmail.trim().toLowerCase();

      // 2. Check if user is trying to invite themselves
      if (emailToInvite === currentUser.email.toLowerCase()) {
        setInviteError('You cannot invite yourself to the project.');
        setInviteLoading(false);
        return;
      }

      // 3. Check if user is already a member of the project
      const projectMembers = await getProjectMembers(projectId);
      if (isUserAlreadyMember(projectMembers, emailToInvite)) {
        setInviteError('This user is already a member of the project. If you want to change his role, access User Management instead');
        setInviteLoading(false);
        return;
      }

      // 4. Check if user exists in the database (beta version requirement)
      const existingUser = await getUserByEmail(emailToInvite, projectId);
      if (!existingUser) {
        setInviteError('This user does not exist in our system. As we are in beta version, please contact the admin to create this user account before sending an invitation.');
        setInviteLoading(false);
        return;
      }

      // 5. Send invitation
      const { inviteLink } = await inviteUserToProject({
        inviterId: currentUser.uid,
        recipientEmail: emailToInvite,
        projectId: projectId,
        roleToAssign: inviteRole
      });

      setInviteSuccess('Invitation sent successfully!');
      setInviteLinkToShow(inviteLink);
      setInviteLoading(false);
      
      // Refresh data to show the new invitation
      fetchData();
    } catch (err) {
      console.error('Error sending invitation:', err);
      if (err.code === 'USER_DISABLED') {
        setInviteError('This user account is disabled. Please contact the admin before sending an invitation.');
      } else {
        setInviteError('Failed to send invitation. Please try again.');
      }
      setInviteLoading(false);
    }
  };

  // Fetch members and invitations
  const fetchData = async () => {
    if (!projectId) return;
    
    setLoading(true);
    setError('');
    
    try {
      // Fetch project members
      const membersRef = collection(db, `projects/${projectId}/members`);
      const membersSnap = await getDocs(membersRef);
      const membersData = membersSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMembers(membersData);

      // Fetch invitations for this project
      const invitationsRef = collection(db, 'invitations');
      const q = query(invitationsRef, where('projectId', '==', projectId));
      const invitationsSnap = await getDocs(q);
      const invitationsData = invitationsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setInvitations(invitationsData);
    } catch (err) {
      console.error('Error fetching users and permissions:', err);
      setError('Failed to load users and permissions data.');
    }
    
    setLoading(false);
  };

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open, projectId]);

  // Handle role change
  const handleRoleChange = async (memberId, newRole) => {
    try {
      // Prevent removing the last owner
      if (newRole !== 'owner') {
        const owners = members.filter(m => m.role === 'owner');
        if (owners.length === 1 && owners[0].id === memberId) {
          setError('Cannot remove the last owner from the project.');
          return;
        }
      }

      // Update member role in project members collection
      const memberRef = doc(db, getProjectMemberPath(projectId, memberId));
      await updateDoc(memberRef, { role: newRole });
      
      // Update user's role in their userProjects collection
      await updateUserProjectRole(memberId, projectId, newRole);
      
      // Update local state
      setMembers(prev => prev.map(m => 
        m.id === memberId ? { ...m, role: newRole } : m
      ));
      
      setSuccess(`Role updated successfully to ${newRole}`);
      setEditingMember(null);
      setNewRole('');
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating role:', err);
      setError('Failed to update role. Please try again.');
    }
  };

  // Handle member removal
  const handleRemoveMember = async (memberId, memberEmail) => {
    if (!window.confirm(`Are you sure you want to remove ${memberEmail} from the project?`)) {
      return;
    }

    try {
      // Prevent removing the last owner
      const member = members.find(m => m.id === memberId);
      if (member.role === 'owner') {
        const owners = members.filter(m => m.role === 'owner');
        if (owners.length === 1) {
          setError('Cannot remove the last owner from the project.');
          return;
        }
      }

      // Remove from project members
      const memberRef = doc(db, getProjectMemberPath(projectId, memberId));
      await deleteDoc(memberRef);
      
      // Remove from user's userProjects collection
      await removeUserProject(memberId, projectId);
      
      // Update local state
      setMembers(prev => prev.filter(m => m.id !== memberId));
      
      setSuccess('Member removed successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error removing member:', err);
      setError('Failed to remove member. Please try again.');
    }
  };

  // Handle invitation removal
  const handleRemoveInvitation = async (invitationId, recipientEmail) => {
    if (!window.confirm(`Are you sure you want to cancel the invitation to ${recipientEmail}?`)) {
      return;
    }

    try {
      const invitationRef = doc(db, 'invitations', invitationId);
      await deleteDoc(invitationRef);
      
      // Update local state
      setInvitations(prev => prev.filter(inv => inv.id !== invitationId));
      
      setSuccess('Invitation cancelled successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error removing invitation:', err);
      setError('Failed to cancel invitation. Please try again.');
    }
  };

  // Handle resend invitation
  const handleResendInvitation = async (invitationId, recipientEmail, roleToAssign) => {
    if (!window.confirm(`Are you sure you want to resend the invitation to ${recipientEmail}?`)) {
      return;
    }

    setResendLoading(true);
    setResendError('');
    setResendSuccess('');

    try {
      // Check if user still exists in the database (beta version requirement)
      const existingUser = await getUserByEmail(recipientEmail, projectId);
      if (!existingUser) {
        setResendError('This user no longer exists in our system. As we are in beta version, please contact the admin to recreate this user account before resending the invitation.');
        setResendLoading(false);
        return;
      }

      // Recreate the invitation with the same stable ID, new token and fresh expiration.
      const { inviteLink } = await inviteUserToProject({
        inviterId: currentUser.uid,
        recipientEmail: recipientEmail,
        projectId: projectId,
        roleToAssign: roleToAssign
      });

      // Update local state - remove old invitation and add new one
      setInvitations(prev => {
        const filtered = prev.filter(inv => inv.id !== invitationId);
        // The new invitation will be fetched in the next fetchData call
        return filtered;
      });

      // Refresh data to show the new invitation
      await fetchData();

      try {
        await navigator.clipboard.writeText(inviteLink);
        setResendSuccess(`Invitation resent. New link copied to clipboard: ${inviteLink}`);
      } catch {
        setResendSuccess(`Invitation resent. New link: ${inviteLink}`);
      }
      setTimeout(() => setResendSuccess(''), 30000);
    } catch (err) {
      console.error('Error resending invitation:', err);
      if (err.code === 'USER_DISABLED') {
        setResendError('This user account is disabled. Please contact the admin before resending the invitation.');
      } else {
        setResendError('Failed to resend invitation. Please try again.');
      }
    } finally {
      setResendLoading(false);
    }
  };

  // Start editing member role
  const startEditingMember = (member) => {
    setEditingMember(member.id);
    setNewRole(member.role);
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingMember(null);
    setNewRole('');
  };

  // Get status chip color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'accepted': return 'success';
      case 'expired': return 'error';
      default: return 'default';
    }
  };

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = toDate(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (loading) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.15)',
              backdropFilter: 'blur(20px)',
            }
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '8px',
            padding: '0 0 8px',
          }}
        >
          Users & Permissions - {projectName}
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              backgroundColor: (theme) => theme.palette.action.hover,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.15)',
            backdropFilter: 'blur(20px)',
          }
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px',
          padding: '0 0 8px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon />
          Users & Permissions - {projectName}
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            backgroundColor: (theme) => theme.palette.action.hover,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent
        sx={{
          padding: '0',
          my: 2,
        }}
      >
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {resendError && <Alert severity="error" sx={{ mb: 2 }}>{resendError}</Alert>}
        {resendSuccess && <Alert severity="success" sx={{ mb: 2 }}>{resendSuccess}</Alert>}

        {/* Project Members Section */}
        <Typography variant="h6" sx={{ mb: 2 }}>
          Project Members ({members.length})
        </Typography>
        
        <TableContainer component={Paper} sx={{ mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{fontWeight: 'bold'}}>
                  <TableSortLabel
                    active={membersSortField === 'email'}
                    direction={membersSortField === 'email' ? membersSortDirection : 'asc'}
                    onClick={() => handleMembersSort('email')}
                  >
                    Email
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{fontWeight: 'bold'}}>
                  <TableSortLabel
                    active={membersSortField === 'role'}
                    direction={membersSortField === 'role' ? membersSortDirection : 'asc'}
                    onClick={() => handleMembersSort('role')}
                  >
                    Role
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{fontWeight: 'bold'}}>
                  <TableSortLabel
                    active={membersSortField === 'joinedAt'}
                    direction={membersSortField === 'joinedAt' ? membersSortDirection : 'asc'}
                    onClick={() => handleMembersSort('joinedAt')}
                  >
                    Joined
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortMembers(members).map((member) => (
                <TableRow key={member.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {member.email}
                      {member.id === currentUser?.uid && (
                        <Chip label="You" size="small" color="primary" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {editingMember === member.id ? (
                      <Select
                        value={newRole}
                        onChange={(e) => setNewRole(e.target.value)}
                        size="small"
                        sx={{ minWidth: 100 }}
                      >
                        <MenuItem value="member">Member</MenuItem>
                        <MenuItem value="owner">Owner</MenuItem>
                      </Select>
                    ) : (
                      <Chip 
                        label={member.role} 
                        color={member.role === 'owner' ? 'primary' : 'default'}
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>{formatDate(member.joinedAt)}</TableCell>
                  <TableCell align="right">
                    {editingMember === member.id ? (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleRoleChange(member.id, newRole)}
                          color="primary"
                        >
                          <CheckIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={cancelEditing}
                          color="error"
                        >
                          <CancelIcon />
                        </IconButton>
                      </Box>
                    ) : (
                      <IconButton
                        size="small"
                        onClick={(event) => handleMembersMenuOpen(event, member.id)}
                        disabled={member.id === currentUser?.uid}
                        title="Actions"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 3 }} />

        {/* Invitations Section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon />
              Invitations ({invitations.filter(inv => inv.status === 'pending').length})
            </Box>
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenAddMemberModal}
            size="small"
          >
            Add Member
          </Button>
        </Box>
        
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{fontWeight: 'bold'}}>
                  <TableSortLabel
                    active={invitationsSortField === 'recipientEmail'}
                    direction={invitationsSortField === 'recipientEmail' ? invitationsSortDirection : 'asc'}
                    onClick={() => handleInvitationsSort('recipientEmail')}
                  >
                    Email
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{fontWeight: 'bold'}}>
                  <TableSortLabel
                    active={invitationsSortField === 'roleToAssign'}
                    direction={invitationsSortField === 'roleToAssign' ? invitationsSortDirection : 'asc'}
                    onClick={() => handleInvitationsSort('roleToAssign')}
                  >
                    Role
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{fontWeight: 'bold'}}>
                  <TableSortLabel
                    active={invitationsSortField === 'status'}
                    direction={invitationsSortField === 'status' ? invitationsSortDirection : 'asc'}
                    onClick={() => handleInvitationsSort('status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{fontWeight: 'bold'}}>
                  <TableSortLabel
                    active={invitationsSortField === 'expiresAt'}
                    direction={invitationsSortField === 'expiresAt' ? invitationsSortDirection : 'asc'}
                    onClick={() => handleInvitationsSort('expiresAt')}
                  >
                    Expires
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invitations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No invitations found
                  </TableCell>
                </TableRow>
              ) : (
                sortInvitations(invitations).map((invitation) => (
                  <TableRow key={invitation.id}>
                    <TableCell>{invitation.recipientEmail}</TableCell>
                    <TableCell>
                      <Chip 
                        label={invitation.roleToAssign} 
                        color={invitation.roleToAssign === 'owner' ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={getInvitationDisplayStatus(invitation)} 
                        color={getStatusColor(getInvitationDisplayStatus(invitation))}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(invitation.expiresAt)}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(event) => handleInvitationsMenuOpen(event, invitation.id)}
                        title="Actions"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      {/* Members Menu */}
      <Menu
        anchorEl={membersMenuAnchorEl}
        open={Boolean(membersMenuAnchorEl)}
        onClose={handleMembersMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 180 } }}
      >
        <MenuItem 
          onClick={() => { 
            const member = members.find(m => m.id === membersMenuMemberId);
            if (member) startEditingMember(member);
            handleMembersMenuClose(); 
          }}
        >
          <EditIcon sx={{ mr: 1 }} />
          Edit Role
        </MenuItem>
        <MenuItem 
          onClick={() => { 
            const member = members.find(m => m.id === membersMenuMemberId);
            if (member) handleRemoveMember(member.id, member.email);
            handleMembersMenuClose(); 
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          Remove Member
        </MenuItem>
      </Menu>

      {/* Invitations Menu */}
      <Menu
        anchorEl={invitationsMenuAnchorEl}
        open={Boolean(invitationsMenuAnchorEl)}
        onClose={handleInvitationsMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 180 } }}
      >
        <MenuItem
          onClick={() => {
            const invitation = invitations.find(inv => inv.id === invitationsMenuInvitationId);
            if (invitation && invitation.status === 'pending' && !isInvitationExpired(invitation)) {
              handleCopyExistingInvitationLink(invitation);
            }
            handleInvitationsMenuClose();
          }}
          disabled={
            !invitations.find(inv =>
              inv.id === invitationsMenuInvitationId &&
              inv.status === 'pending' &&
              !isInvitationExpired(inv) &&
              inv.token
            )
          }
        >
          <ContentCopyIcon sx={{ mr: 1 }} />
          Copy Invite Link
        </MenuItem>
        <MenuItem 
          onClick={() => { 
            const invitation = invitations.find(inv => inv.id === invitationsMenuInvitationId);
            if (invitation) {
              handleResendInvitation(invitation.id, invitation.recipientEmail, invitation.roleToAssign);
            }
            handleInvitationsMenuClose(); 
          }}
          disabled={resendLoading}
        >
          <RefreshIcon sx={{ mr: 1 }} />
          {resendLoading ? 'Resending...' : 'Resend Invitation'}
        </MenuItem>
        <MenuItem 
          onClick={() => { 
            const invitation = invitations.find(inv => inv.id === invitationsMenuInvitationId);
            if (invitation) {
              handleRemoveInvitation(invitation.id, invitation.recipientEmail);
            }
            handleInvitationsMenuClose(); 
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          Cancel Invitation
        </MenuItem>
      </Menu>

      {/* Add Member Modal */}
      <Dialog
        open={addMemberModalOpen}
        onClose={handleCloseAddMemberModal}
        maxWidth="xs"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.15)',
              backdropFilter: 'blur(20px)',
            }
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '8px',
            padding: '0 0 8px',
          }}
        >
          Invite Member to Project
          <IconButton
            onClick={handleCloseAddMemberModal}
            size="small"
            sx={{
              backgroundColor: (theme) => theme.palette.action.hover,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            padding: '0',
            my: 2,
          }}
        >
          {inviteError && <Alert severity="error" sx={{ mb: 2 }}>{inviteError}</Alert>}
          {inviteSuccess && <Alert severity="success" sx={{ mb: 2 }}>{inviteSuccess}</Alert>}
          {inviteLinkToShow && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={handleCopyInviteLink}>
                  Copy Link
                </Button>
              }
            >
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                {inviteLinkToShow}
              </Typography>
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="User Email"
            type="email"
            fullWidth
            value={inviteEmail}
            onChange={e => setInviteEmail(e.target.value)}
            disabled={inviteLoading}
            error={inviteEmail && !validateEmail(inviteEmail.trim())}
            helperText={inviteEmail && !validateEmail(inviteEmail.trim()) ? 'Please enter a valid email address' : ''}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="invite-role-label">Role</InputLabel>
            <Select
              labelId="invite-role-label"
              value={inviteRole}
              label="Role"
              onChange={e => setInviteRole(e.target.value)}
              disabled={inviteLoading}
            >
              <MenuItem value="member">Member</MenuItem>
              <MenuItem value="owner">Owner</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: "16px 0 0" }}>
          <Button 
            onClick={handleInvite} 
            variant="contained" 
            disabled={inviteLoading || !inviteEmail.trim() || !validateEmail(inviteEmail.trim())}
          >
            {inviteLoading ? <CircularProgress size={18} /> : 'Invite'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

UsersPermissionsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  projectName: PropTypes.string.isRequired,
  currentUser: PropTypes.shape({
    uid: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
  }).isRequired,
};

export default UsersPermissionsDialog; 
